/**
 * La mascota flotante: abajo a la derecha, en un círculo blanco, en todas las
 * secciones del panel —y en Inicio, en cuanto la de arriba sale de la vista—.
 * Al pulsarla se abre el globo con la conversación (la misma de Inicio).
 *
 * No se cierra al pulsar fuera, a propósito: lo que contesta suele ser «te
 * llevo a Cupones», y la sección cambia detrás mientras sigues hablando con
 * ella. Se cierra con la ✕, con Escape o pulsándola otra vez.
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react'
import { IconRefresh, IconX } from '@tabler/icons-react'
import { AiMascot } from '@webbuilder/ui'
import { useI18n } from '../../lib/i18n'
import { clearAssistant, setAssistantOpen, useAssistant } from '../../lib/dashboardAssistant'
import { AssistantChat } from './AssistantChat'

export function FloatingAssistant({ onHome, userName, avatarUrl, anchorRef }: {
  onHome: boolean
  userName: string
  avatarUrl: string
  /**
   * La tarjeta grande del contenido. La mascota se sienta en SU esquina, no en
   * la de la ventana: pegada a la ventana tapaba el pie del panel (idioma,
   * ayuda, lo legal), que vive fuera de la tarjeta.
   */
  anchorRef: RefObject<HTMLElement | null>
}) {
  const { t } = useI18n()
  const { open, busy, homeVisible, messages } = useAssistant()
  const triggerRef = useRef<HTMLButtonElement>(null)
  /* En Inicio, con la mascota de arriba a la vista, sobra — salvo que se haya
     abierto a propósito («Ver conversación completa»). */
  const hidden = onHome && homeVisible && !open
  const [anchor, setAnchor] = useState<{ right: number; bottom: number; height: number } | null>(null)

  useLayoutEffect(() => {
    const node = anchorRef.current
    if (!node || hidden) return
    const measure = () => {
      const rect = node.getBoundingClientRect()
      /* Lo que se VE de la tarjeta: si se alarga más allá de la ventana, su
         canto de abajo es el de la ventana. */
      const bottom = Math.min(rect.bottom, window.innerHeight)
      const top = Math.max(rect.top, 0)
      setAnchor({ right: Math.max(0, window.innerWidth - rect.right), bottom: Math.max(0, window.innerHeight - bottom), height: Math.max(0, bottom - top) })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    window.addEventListener('resize', measure)
    /* El pie entra y sale al llegar al final: la tarjeta sube con él. */
    window.addEventListener('scroll', measure, { passive: true, capture: true })
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, { capture: true })
    }
  }, [anchorRef, hidden])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setAssistantOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  if (hidden) return null

  return (
    <div
      className={`dash-float-assist${open ? ' is-open' : ''}`}
      style={anchor ? {
        '--dash-float-right': `${anchor.right}px`,
        '--dash-float-bottom': `${anchor.bottom}px`,
        '--dash-float-room': `${anchor.height}px`,
      } as CSSProperties : undefined}
    >
      {open ? (
        <section className="dash-float-assist-panel" role="dialog" aria-label={t('assistant.title')}>
          <header className="dash-float-assist-head">
            <span className="dash-float-assist-title">{t('assistant.title')}</span>
            {messages.length ? (
              <button type="button" className="dash-float-assist-icon" onClick={clearAssistant} disabled={busy} aria-label={t('assistant.clear')} title={t('assistant.clear')}>
                <IconRefresh size={15} aria-hidden="true" />
              </button>
            ) : null}
            <button type="button" className="dash-float-assist-icon" onClick={() => { setAssistantOpen(false); triggerRef.current?.focus() }} aria-label={t('assistant.close')}>
              <IconX size={15} aria-hidden="true" />
            </button>
          </header>
          <AssistantChat variant="floating" autoFocus userName={userName} avatarUrl={avatarUrl} />
        </section>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        className="dash-float-assist-trigger"
        aria-label={open ? t('assistant.close') : t('assistant.open')}
        aria-expanded={open}
        onClick={() => setAssistantOpen(!open)}
      >
        <AiMascot working={busy} />
      </button>
    </div>
  )
}
